import { yupResolver } from '@hookform/resolvers/yup';
import { TabButtonGroup } from '@zorro/design-system/v2/TabButtonGroup';
import { Title } from '@zorro/design-system/v2/Title';
import { Group, InputWrapper, Stack } from '@zorro/web/design-system';
import { useForm } from '@zorro/web/ui-utils';
import { ReactElement } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';

import { InviteByEmailDecoration } from './InviteByEmailDecoration';
import { InviteByEmailForm } from './InviteByEmailForm';
import { InviteByLinkDecoration } from './InviteByLinkDecoration';
import { InviteByLinkForm } from './InviteByLinkForm';
import {
  InvitationType,
  SendInviteFormFields,
  sendInviteSchema,
} from './SendInviteUtils';

const sendInviteModalConfig: Record<
  InvitationType,
  {
    rightSideDecoration: ReactElement;
    renderForm: (
      employerId: string,
      sendInviteForm: UseFormReturn<SendInviteFormFields>,
      onSubmit?: () => void
    ) => ReactElement;
  }
> = {
  [InvitationType.BY_EMAIL]: {
    rightSideDecoration: <InviteByEmailDecoration />,
    renderForm: (employerId, sendInviteForm, onSubmit) => (
      <InviteByEmailForm
        sendInviteForm={sendInviteForm}
        employerId={employerId}
        onSubmit={onSubmit}
      />
    ),
  },
  [InvitationType.BY_LINK]: {
    rightSideDecoration: <InviteByLinkDecoration />,
    renderForm: (employerId, sendInviteForm) => (
      <InviteByLinkForm
        sendInviteForm={sendInviteForm}
        employerId={employerId}
      />
    ),
  },
};

type Props = {
  employerId: string;
  employerName: string;
  onClose: () => void;
};

export const SendInviteForm = ({
  employerId,
  employerName,
  onClose,
}: Props) => {
  'use no memo';
  const sendInviteForm = useForm<SendInviteFormFields>({
    mode: 'all',
    resolver: yupResolver(sendInviteSchema),
    defaultValues: {
      invitationType: InvitationType.BY_EMAIL,
      email: '',
    },
  });

  const invitationType = sendInviteForm.watch('invitationType');

  const { rightSideDecoration, renderForm } =
    sendInviteModalConfig[invitationType];

  return (
    <Group wrap="nowrap" h="567px" gap="1.5rem" align="start">
      <Stack flex={1} miw={0} ml="2.5rem" mt="2.5rem" gap="xl">
        <Title>Invite {employerName} to complete their company profile</Title>
        <Controller
          control={sendInviteForm.control}
          name="invitationType"
          render={({ field: { ref: _ref, onChange, ...rest } }) => (
            <InputWrapper w="200px">
              <TabButtonGroup {...rest} onChange={onChange}>
                <TabButtonGroup.List grow>
                  <TabButtonGroup.Tab value={InvitationType.BY_EMAIL}>
                    By email
                  </TabButtonGroup.Tab>
                  <TabButtonGroup.Tab value={InvitationType.BY_LINK}>
                    By link
                  </TabButtonGroup.Tab>
                </TabButtonGroup.List>
              </TabButtonGroup>
            </InputWrapper>
          )}
        />

        {renderForm(employerId, sendInviteForm, onClose)}
      </Stack>
      <Stack
        style={{ borderRadius: '8px', flexShrink: 0 }}
        p="2rem 2rem 0 2rem"
        pos="relative"
        gap="2.75rem"
        bg="#EDEFF6"
        w="480px"
        h="100%"
        ml="8px"
      >
        {rightSideDecoration}
      </Stack>
    </Group>
  );
};
